import { useState } from 'react'
import { Radio, RadioGroup } from '../Radio'
import { Text } from '../Text'
import { FormLabelWithTooltip } from './FormLabelWithTooltip'
import type { ChoicesFieldCustomProps, ComponentProps } from './types'

type FormsChoicesFieldProps = ComponentProps & {
	value?: string
	onChange?: (value: string) => void
	readOnly?: boolean
	hasError?: boolean
}

export function ChoiceSelectField({ component, onChange, value, readOnly, required }: FormsChoicesFieldProps) {
	const { label, labelSize, labelWeight, labelColor, defaultChoice, options, tooltipEnabled, tooltipText } =
		component.custom as ChoicesFieldCustomProps
	const [internalValue, setInternalValue] = useState(defaultChoice || '')

	const handleChange = (val: string) => {
		if (onChange) {
			onChange(val)
		} else {
			setInternalValue(val)
		}
	}

	const selectOptions =
		(typeof options === 'string' &&
			options
				.split(',')
				.filter((o) => o.trim())
				.map((o) => ({ value: o.trim(), label: o.trim() }))) ||
		(Array.isArray(options) && options.map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }))) ||
		[]

	return (
		<div id={component.id} className='flex flex-col gap-1'>
			{label && (
				<FormLabelWithTooltip
					label={label}
					labelColor={labelColor}
					labelSize={labelSize}
					labelWeight={labelWeight}
					required={required}
					readOnly={readOnly}
					tooltipEnabled={tooltipEnabled}
					tooltipText={tooltipText}
				/>
			)}
			{readOnly ? (
				<div className='flex items-center gap-1'>
					<div className='bg-neutral-50 border border-neutral-200 rounded-md px-6 py-4'>
						<Text>{value || ''}</Text>
					</div>
				</div>
			) : (
				<RadioGroup
					name='choices-field'
					value={value ?? internalValue}
					onChange={handleChange}
					defaultValue={defaultChoice}>
					{selectOptions.map((option) => (
						<Radio
							key={option.value || option.label}
							value={option.value ? option.value : option.label}
							label={option.label}
						/>
					))}
				</RadioGroup>
			)}
		</div>
	)
}
